/**
 * Activity cell formatters for DataGrid and ActivityTableRow.
 * Pure functions so they can be used in column definitions without hooks.
 * Uses preferred date format from user settings (see @/utils/dateFormat).
 */

import type { ScheduleActivity } from '@/types';
import type { ColumnState } from './types';
import { NUMERIC_KEYS } from './types';
import { formatDateByPreference, type PreferredDateFormat } from '@/utils/dateFormat';

export type DateFormat = PreferredDateFormat;

export interface FormatterConfig {
  dateFormat: DateFormat;
  timeZone?: string;
  columnState: ColumnState[];
  showTime: boolean;
  floatDisplayUnit: 'hours' | 'days';
}

function formatDate(
  dateString: string | null | undefined,
  showTime: boolean,
  dateFormat: DateFormat,
  timeZone?: string
): string {
  return formatDateByPreference(dateString, dateFormat, { showTime, timeZone });
}

/** Format XER float (hours) with explicit unit: "d" (days, hrs/8) or "hr" (hours). */
function formatFloatWithUnit(
  value: number | null | undefined,
  config: FormatterConfig,
  unit: 'd' | 'hr'
): string {
  if (value === null || value === undefined) return '-';
  const floatConfig = config.columnState.find(
    (col) => col.key === 'total_float_hr_cnt' || col.key === 'total_float'
  );
  const decimalsEnabled = floatConfig?.decimalsEnabled ?? false;
  const decimalPlaces = floatConfig?.decimalPlaces ?? 2;
  if (unit === 'd') {
    const days = value / 8;
    const str = decimalsEnabled ? days.toFixed(decimalPlaces) : Math.round(days).toString();
    return `${str} d`;
  }
  const str = decimalsEnabled ? value.toFixed(decimalPlaces) : Math.round(value).toString();
  return `${str} hr`;
}

function formatFloat(
  value: number | null | undefined,
  config: FormatterConfig
): string {
  const unit = config.floatDisplayUnit === 'days' ? 'd' : 'hr';
  return formatFloatWithUnit(value, config, unit);
}

function formatNumeric(
  value: number | null | undefined,
  key: string,
  config: FormatterConfig
): string {
  if (value === null || value === undefined) return '-';
  const columnConfig = config.columnState.find((col) => col.key === key);
  const decimalsEnabled = columnConfig?.decimalsEnabled ?? false;
  const decimalPlaces = columnConfig?.decimalPlaces ?? 2;
  if (key === 'total_float_hr_cnt' || key === 'total_float') return formatFloat(value, config);
  return decimalsEnabled ? value.toFixed(decimalPlaces) : Math.round(value).toString();
}

function formatPercentage(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-';
  return `${Math.round(value)}%`;
}

function formatDuration(
  value: number | null | undefined,
  floatDisplayUnit: 'hours' | 'days'
): string {
  if (value === null || value === undefined) return '-';
  if (floatDisplayUnit === 'hours') {
    const hours = value * 8;
    if (hours < 1) return `${(hours * 60).toFixed(0)} min`;
    return `${hours.toFixed(1)} hrs`;
  }
  if (value < 1) {
    const hours = value * 8;
    return `${hours.toFixed(1)} hrs`;
  }
  return `${value.toFixed(2)} days`;
}

export function makeGetCellValue(config: FormatterConfig) {
  return (activity: ScheduleActivity, key: string): string => {
    const value = (activity as Record<string, unknown>)[key];
    if (key.includes('_date')) return formatDate(String(value ?? ''), config.showTime, config.dateFormat, config.timeZone);
    if (key === 'total_float_hr_cnt') return formatFloat(value as number, config);
  if (key === 'total_float') {
    const hrs = (activity as Record<string, unknown>).total_float_hr_cnt;
    if (hrs != null) return formatFloatWithUnit(hrs as number, config, config.floatDisplayUnit === 'days' ? 'd' : 'hr');
    const days = (activity as Record<string, unknown>).total_float;
    if (days != null) return config.floatDisplayUnit === 'days' ? `${Number(days).toFixed(2)} d` : formatFloatWithUnit(Number(days) * 8, config, 'hr');
    return '-';
  }
  if (key === 'free_float') {
    const hrs = (activity as Record<string, unknown>).free_float_hr_cnt;
    if (hrs != null) return formatFloatWithUnit(hrs as number, config, config.floatDisplayUnit === 'days' ? 'd' : 'hr');
    const days = (activity as Record<string, unknown>).free_float;
    if (days != null) return config.floatDisplayUnit === 'days' ? `${Number(days).toFixed(2)} d` : formatFloatWithUnit(Number(days) * 8, config, 'hr');
    return '-';
  }
    if (key === 'duration' || key === 'original_duration' || key === 'remaining_duration') {
      return formatDuration(value as number, config.floatDisplayUnit);
    }
    if (key.includes('_pct') || key.includes('_complete')) return formatPercentage(value as number);
    if (NUMERIC_KEYS.includes(key)) return formatNumeric(value as number, key, config);
    if (key === 'is_critical') return value ? 'Yes' : 'No';
    return value != null ? String(value) : '-';
  };
}

export function getCellClassName(activity: ScheduleActivity, key: string): string {
  const value = (activity as Record<string, unknown>)[key];
  if (key === 'is_critical' && value) return 'text-red-400 font-semibold';
  if (key === 'total_float' || key === 'total_float_hr_cnt' || key === 'free_float') {
    const hrs = key === 'free_float'
      ? (activity as Record<string, unknown>).free_float_hr_cnt
      : (activity as Record<string, unknown>).total_float_hr_cnt;
    const n = hrs != null ? Number(hrs) : (value != null ? Number(value) * 8 : null);
    if (n != null && n < 0) return 'text-red-400 font-semibold';
    if (n != null && n < 8) return 'text-yellow-400 font-semibold';
  }
  if (key.includes('_pct') && value !== null && value !== undefined) {
    const n = Number(value);
    if (n >= 100) return 'text-green-400 font-semibold';
    if (n >= 75) return 'text-blue-400';
  }
  return '';
}
